import React, { useState, useEffect } from 'react';

// Backend API URL
const API_URL = 'https://hr-scanner-backend-production.up.railway.app';

// Logo Component with Lock
const Logo = () => (
  <div className="flex items-center space-x-2">
    <svg className="h-8 w-8 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2"/>
      <path d="M7 11V7a5 5 0 0110 0v4" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="12" cy="16" r="1.5" fill="currentColor"/>
    </svg>
    <span className="text-2xl font-bold text-blue-600">HR Scanner Pro</span>
  </div>
);

// Footer Component with Legal Links
const Footer = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [modalTitle, setModalTitle] = useState('');

  const openModal = (title, content) => {
    setModalTitle(title);
    setModalContent(content);
    setShowModal(true);
  };

  return (
    <>
      <div className="w-full py-6 text-center border-t border-gray-200 bg-white mt-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0 md:space-x-6 text-sm text-gray-600 mb-3">
            <button 
              onClick={() => openModal('Privacy Policy', 'privacy')}
              className="hover:text-blue-600 underline"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => openModal('Terms of Service', 'terms')}
              className="hover:text-blue-600 underline"
            >
              Terms of Service
            </button>
            <button 
              onClick={() => openModal('Cookie Policy', 'cookies')}
              className="hover:text-blue-600 underline"
            >
              Cookie Policy
            </button>
            <button 
              onClick={() => openModal('Refund Policy', 'refund')}
              className="hover:text-blue-600 underline"
            >
              Refund Policy
            </button>
          </div>
          <p className="text-sm text-gray-600">
            Powered by{' '}
            <a 
              href="https://easeaiworks.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 font-semibold"
            >
              EaseAIworks.com
            </a>
          </p>
          <p className="text-xs text-gray-500 mt-2">
            © 2025 HR Scanner Pro. All rights reserved.
          </p>
        </div>
      </div>

      {showModal && (
        <LegalModal 
          title={modalTitle}
          contentType={modalContent}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

// Legal Modal Component
const LegalModal = ({ title, contentType, onClose }) => {
  const getContent = () => {
    if (contentType === 'privacy') {
      return (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-blue-800">
              <strong>Our Zero-Retention Privacy Model:</strong>
            </p>
          </div>
          <div className="prose max-w-none">
            <h2>Privacy Policy Overview</h2>
            <p><strong>Last Updated:</strong> October 12, 2025</p>
            
            <h3>What We Store:</h3>
            <ul>
              <li>✓ Your account information (email, name)</li>
              <li>✓ Credit balance and transaction history</li>
            </ul>

            <h3>What We NEVER Store:</h3>
            <ul>
              <li>✗ Resume content or text</li>
              <li>✗ Candidate names, addresses, phone numbers</li>
              <li>✗ Job descriptions</li>
              <li>✗ Scan results</li>
            </ul>

            <p className="font-semibold text-lg mt-4">
              All candidate data is processed in real-time and immediately deleted.
            </p>

            <h3>Compliance:</h3>
            <ul>
              <li>🇨🇦 PIPEDA compliant (Canada)</li>
              <li>🇺🇸 CCPA/CPRA compliant (USA)</li>
            </ul>

            <h3>Your Rights:</h3>
            <p>You have the right to:</p>
            <ul>
              <li>Access your personal information</li>
              <li>Request correction of inaccurate information</li>
              <li>Delete your account at any time</li>
              <li>File a complaint with privacy regulators</li>
            </ul>

            <h3>Data Security:</h3>
            <p>We use:</p>
            <ul>
              <li>TLS 1.3 encryption for all data in transit</li>
              <li>Secure authentication via Supabase</li>
              <li>Regular security audits</li>
              <li><strong>Zero-storage model</strong> for candidate data (cannot be breached)</li>
            </ul>

            <h3>Service Providers:</h3>
            <p>We share data only with:</p>
            <ul>
              <li>Anthropic (Claude AI) - real-time resume processing</li>
              <li>Stripe - payment processing (PCI-DSS compliant)</li>
              <li>Supabase - authentication (ISO 27001 certified)</li>
              <li>Railway - hosting infrastructure</li>
            </ul>
            
            <div className="mt-6 p-4 bg-gray-50 rounded">
              <p className="font-semibold mb-2">Contact Information:</p>
              <p><strong>Data Privacy Officer</strong></p>
              <p>EaseAIworks.com / HR Scanner Pro</p>
              <p>Email: <a href="mailto:info@hrscanpro.com" className="text-blue-600 underline">info@hrscanpro.com</a></p>
              <p>Address: Burlington, Ontario, Canada</p>
              
              <p className="mt-4 text-sm">
                <strong>For Canadian Users:</strong><br />
                Office of the Privacy Commissioner of Canada<br />
                Website: www.priv.gc.ca | Toll-free: 1-800-282-1376
              </p>
              
              <p className="mt-2 text-sm">
                <strong>For California Residents:</strong><br />
                California Attorney General's Office<br />
                Website: oag.ca.gov/privacy
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (contentType === 'terms') {
      return (
        <div className="prose max-w-none">
          <h2>Terms of Service</h2>
          <p><strong>Effective Date:</strong> October 12, 2025</p>
          
          <h3>1. Acceptance of Terms</h3>
          <p>By using HR Scanner Pro, you agree to these Terms of Service and our Privacy Policy.</p>

          <h3>2. Description of Service</h3>
          <p>HR Scanner Pro is an AI-powered resume screening tool that:</p>
          <ul>
            <li>Analyzes resumes against job descriptions</li>
            <li>Provides weighted scoring and candidate rankings</li>
            <li>Operates on a credit-based system</li>
            <li><strong>Does not store candidate or resume data</strong></li>
          </ul>

          <h3>3. Credits and Pricing</h3>
          <p><strong>Current Packages:</strong></p>
          <ul>
            <li>Starter: 100 scans - $15 ($0.15/scan)</li>
            <li>Professional: 500 scans - $40 ($0.08/scan)</li>
            <li>Business: 1,500 scans - $100 ($0.067/scan)</li>
            <li>Enterprise: 5,000 scans - $275 ($0.055/scan)</li>
          </ul>
          <p>New users receive <strong>50 FREE credits</strong> upon signup.</p>

          <h3>4. Prohibited Use</h3>
          <p>You may NOT:</p>
          <ul>
            <li>Use the Service for unlawful discrimination</li>
            <li>Violate employment or privacy laws</li>
            <li>Share or resell access</li>
            <li>Reverse engineer our technology</li>
          </ul>

          <h3>5. Your Responsibilities</h3>
          <p>You must:</p>
          <ul>
            <li>Comply with applicable privacy laws (PIPEDA, CCPA, etc.)</li>
            <li>Obtain necessary consents from job applicants</li>
            <li>Include appropriate disclosures in job postings</li>
          </ul>
          <p><strong>Example disclosure:</strong> "Applications will be screened using AI technology. Candidate data is processed in real-time and not stored."</p>

          <h3>6. Limitation of Liability</h3>
          <p>We are NOT liable for:</p>
          <ul>
            <li>Hiring decisions made using our Service</li>
            <li>AI accuracy or suitability of candidates</li>
            <li>Third-party service failures</li>
          </ul>
          <p><strong>Maximum Liability:</strong> Limited to amount paid in preceding 12 months.</p>

          <h3>7. Governing Law</h3>
          <ul>
            <li><strong>Canadian Users:</strong> Governed by laws of Ontario, Canada</li>
            <li><strong>US Users:</strong> Governed by laws of New York, United States</li>
          </ul>

          <div className="mt-6 p-4 bg-gray-50 rounded">
            <p className="font-semibold mb-2">Contact:</p>
            <p>Email: <a href="mailto:info@hrscanpro.com" className="text-blue-600 underline">info@hrscanpro.com</a></p>
            <p>Address: Burlington, Ontario, Canada</p>
          </div>
        </div>
      );
    }

    if (contentType === 'cookies') {
      return (
        <div className="prose max-w-none">
          <h2>Cookie Policy</h2>
          <p><strong>Effective Date:</strong> October 12, 2025</p>

          <h3>1. What Are Cookies?</h3>
          <p>Cookies are small text files stored on your device to help us provide a better user experience.</p>

          <h3>2. Cookies We Use</h3>
          
          <h4>Strictly Necessary Cookies</h4>
          <ul>
            <li><strong>Purpose:</strong> Essential for the Service to function</li>
            <li><strong>Examples:</strong> Authentication tokens, security verification</li>
            <li><strong>Opt-Out:</strong> Not possible (Service won't work)</li>
          </ul>

          <h4>Analytics Cookies (If Applicable)</h4>
          <ul>
            <li><strong>Purpose:</strong> Understand user behavior</li>
            <li><strong>Opt-Out:</strong> Available in browser settings</li>
          </ul>

          <h3>3. Third-Party Cookies</h3>
          <p>We use services that may set cookies:</p>
          <ul>
            <li><strong>Stripe:</strong> Payment processing (necessary)</li>
            <li><strong>Supabase:</strong> Authentication (necessary)</li>
          </ul>

          <h3>4. How to Control Cookies</h3>
          <p>Most browsers allow you to:</p>
          <ul>
            <li>View and delete cookies</li>
            <li>Block all cookies</li>
            <li>Block third-party cookies</li>
          </ul>
          <p><strong>Note:</strong> Blocking necessary cookies will prevent the Service from working.</p>

          <div className="mt-6 p-4 bg-gray-50 rounded">
            <p className="font-semibold mb-2">Contact:</p>
            <p>Email: <a href="mailto:info@hrscanpro.com" className="text-blue-600 underline">info@hrscanpro.com</a></p>
            <p>Address: Burlington, Ontario, Canada</p>
          </div>
        </div>
      );
    }

    if (contentType === 'refund') {
      return (
        <div className="prose max-w-none">
          <h2>Refund Policy</h2>
          <p><strong>Effective Date:</strong> October 12, 2025</p>

          <h3>1. Free Credits (50 Welcome Bonus)</h3>
          <ul>
            <li>Non-refundable</li>
            <li>No cash value</li>
            <li>Cannot be transferred</li>
          </ul>

          <h3>2. Purchased Credits - 7-Day Refund Window</h3>
          <p><strong>You may request a full refund within 7 days IF:</strong></p>
          <ul>
            <li>You've used less than 10% of purchased credits</li>
            <li>The Service is materially defective</li>
            <li>This is your first refund request</li>
          </ul>

          <h3>3. What Is NOT Refundable</h3>
          <ul>
            <li>More than 7 days have passed</li>
            <li>You've used more than 10% of credits</li>
            <li>Dissatisfaction with AI results (subjective)</li>
            <li>User error (wrong files, incorrect job description)</li>
            <li>Change of mind after using the Service</li>
          </ul>

          <h3>4. How to Request a Refund</h3>
          <p><strong>Email:</strong> info@hrscanpro.com</p>
          <p><strong>Include:</strong></p>
          <ul>
            <li>Account email</li>
            <li>Purchase date and transaction ID</li>
            <li>Credits purchased and used</li>
            <li>Reason for refund</li>
          </ul>

          <h3>5. Processing Time</h3>
          <ul>
            <li><strong>Response:</strong> 2-3 business days</li>
            <li><strong>Refund:</strong> 5-10 business days (if approved)</li>
          </ul>

          <h3>6. Technical Issues</h3>
          <p>If our system fails to process scans:</p>
          <ul>
            <li>Contact us immediately at info@hrscanpro.com</li>
            <li>We'll attempt to resolve within 48 hours</li>
            <li>Prorated refund available if unresolved</li>
          </ul>

          <h3>7. Chargeback Policy</h3>
          <p><strong>Please contact us before filing a chargeback.</strong></p>
          <p>Chargebacks may result in account suspension and prevent future purchases.</p>

          <div className="mt-6 p-4 bg-gray-50 rounded">
            <p className="font-semibold mb-2">Contact:</p>
            <p>Email: <a href="mailto:info@hrscanpro.com" className="text-blue-600 underline">info@hrscanpro.com</a></p>
            <p>Subject: "Refund Request - [Your Email]"</p>
            <p className="mt-2">Address: Burlington, Ontario, Canada</p>
          </div>
        </div>
      );
    }

    return <p>Content not found.</p>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b flex justify-between items-center bg-blue-600 text-white">
          <h2 className="text-2xl font-bold">{title}</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-3xl font-bold leading-none"
          >
            ×
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto flex-grow">
          {getContent()}
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [currentUser, setCurrentUser] = useState(null);
  const [userCredits, setUserCredits] = useState(50);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Scanner states
  const [jobDescription, setJobDescription] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [keywords, setKeywords] = useState('');
  const [scanResults, setScanResults] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showSummaryView, setShowSummaryView] = useState(false);
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  
  // Credit packages
  const creditPackages = [
    {
      id: 1,
      name: 'Starter',
      scans: 100,
      price: 15,
      description: 'Perfect for small teams',
      popular: false
    },
    {
      id: 2,
      name: 'Professional',
      scans: 500,
      price: 40,
      description: 'Most popular choice',
      popular: true
    },
    {
      id: 3,
      name: 'Business',
      scans: 1500,
      price: 100,
      description: 'For growing companies',
      popular: false
    },
    {
      id: 4,
      name: 'Enterprise',
      scans: 5000,
      price: 275,
      description: 'High-volume recruiting',
      popular: false
    }
  ];
  
  useEffect(() => {
    // Check if returning from Stripe payment
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_id')) {
      setCurrentPage('success');
    }
    // Check for existing session on page load
    const token = localStorage.getItem('authToken');
    const email = localStorage.getItem('userEmail');
    
    if (token && email) {
      setCurrentUser({ email, id: 'loading' });
      fetchUserCredits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchPurchaseDetails = async () => {
      if (currentPage === 'success') {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        
        if (sessionId) {
          try {
            const response = await fetch(`${API_URL}/api/verify-session/${sessionId}`);
            const data = await response.json();
            
            if (data.success) {
              setPurchaseDetails(data);
              // Refresh credits
              fetchUserCredits();
            }
          } catch (error) {
            console.error('Error verifying purchase:', error);
          }
        }
      }
    };
    
    fetchPurchaseDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchUserCredits = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const email = localStorage.getItem('userEmail');
      
      if (!token || !email) return;
      
      const response = await fetch(`${API_URL}/api/user/${email}/credits`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      const data = await response.json();
      setUserCredits(data.credits);
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    
    try {
      let authResponse;
      
      if (currentPage === 'signup') {
        // Sign up new user
        const response = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            firstName: formData.firstName || '',
            lastName: formData.lastName || '',
            company: formData.company || ''
          })
        });
        
        const data = await response.json();
        if (data.error) throw new Error(data.error);
        authResponse = data;
      } else {
        // Login existing user
        const response = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          })
        });
        
        const data = await response.json();
        if (data.error) throw new Error(data.error);
        authResponse = data;
      }
      
      // Store session token in localStorage
      localStorage.setItem('authToken', authResponse.session.access_token);
      localStorage.setItem('userEmail', authResponse.user.email);
      
      setCurrentUser({
        email: authResponse.user.email,
        id: authResponse.user.id
      });
      setUserCredits(authResponse.user.credits_balance || 50);
      setCurrentPage('dashboard');
      
    } catch (error) {
      console.error('Auth error:', error);
      alert(`Authentication failed: ${error.message}`);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    setCurrentUser(null);
    setUserCredits(0);
    setCurrentPage('landing');
  };

  // Purchase credits via Stripe
  const handlePurchaseCredits = async (packageId) => {
    const selectedPackage = creditPackages.find(pkg => pkg.id === packageId);
    
    try {
      const response = await fetch(`${API_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credits: selectedPackage.scans,
          amount: selectedPackage.price * 100,
          currency: 'usd',
          user_email: currentUser.email,
          user_id: currentUser.id,
          package_name: selectedPackage.name
        })
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to initiate payment. Please try again.');
    }
  };

  // ============================================================
  // UPDATED FUNCTION: Handle file upload - PDF, DOC, DOCX PARSING
  // ============================================================
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    // Dynamically import parsers
    const pdfjsLib = await import('pdfjs-dist');
    const mammoth = await import('mammoth');
    
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
    
    const parsedResumes = [];
    const failedFiles = [];

    for (const file of files) {
      try {
        let content = '';
        const fileExtension = file.name.split('.').pop().toLowerCase();
        
        // PDF PARSING
        if (file.type === 'application/pdf' || fileExtension === 'pdf') {
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            content += pageText + ' ';
          }
          
          // Check if PDF had any text
          if (content.trim().length === 0) {
            failedFiles.push({ 
              name: file.name, 
              reason: 'PDF appears to be scanned/image-based with no extractable text. Please use a PDF with text content.' 
            });
            continue;
          }
        } 
        // WORD DOCUMENT PARSING (.docx)
        else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                 fileExtension === 'docx') {
          const arrayBuffer = await file.arrayBuffer();
          const result = await mammoth.extractRawText({ arrayBuffer: arrayBuffer });
          content = result.value;
          
          if (content.trim().length === 0) {
            failedFiles.push({ 
              name: file.name, 
              reason: 'Word document appears to be empty or contains only images.' 
            });
            continue;
          }
        }
        // OLDER WORD DOCUMENT (.doc)
        else if (file.type === 'application/msword' || fileExtension === 'doc') {
          // Note: .doc (older format) is harder to parse. Mammoth works best with .docx
          // Try to parse anyway
          const arrayBuffer = await file.arrayBuffer();
          const result = await mammoth.extractRawText({ arrayBuffer: arrayBuffer });
          content = result.value;
          
          if (content.trim().length === 0) {
            failedFiles.push({ 
              name: file.name, 
              reason: 'Unable to extract text from .doc file. Please save as .docx or PDF for best results.' 
            });
            continue;
          }
        }
        // UNSUPPORTED FILE TYPE
        else {
          failedFiles.push({ 
            name: file.name, 
            reason: `Unsupported file type: ${file.type || 'unknown'}. Please use PDF, DOC, or DOCX.` 
          });
          continue;
        }
        
        // Successfully parsed - add to results
        if (content.trim().length > 0) {
          parsedResumes.push({
            name: file.name,
            content: content.trim()
          });
        }
      } catch (error) {
        console.error(`Error parsing ${file.name}:`, error);
        failedFiles.push({ 
          name: file.name, 
          reason: `Failed to parse: ${error.message}` 
        });
      }
    }
    
    // Show detailed feedback to user
    if (failedFiles.length > 0 && parsedResumes.length > 0) {
      // Some succeeded, some failed
      alert(
        `✅ Successfully uploaded ${parsedResumes.length} resume(s)\n\n` +
        `❌ Failed to process ${failedFiles.length} file(s):\n\n` +
        failedFiles.map(f => `• ${f.name}\n  ${f.reason}`).join('\n\n') +
        `\n\nOnly the ${parsedResumes.length} successfully parsed file(s) will be scanned.`
      );
    } else if (failedFiles.length > 0 && parsedResumes.length === 0) {
      // All failed
      alert(
        `❌ Could not process any files:\n\n` +
        failedFiles.map(f => `• ${f.name}\n  ${f.reason}`).join('\n\n') +
        `\n\nPlease check your files and try again.`
      );
    } else if (parsedResumes.length > 0) {
      // All succeeded - show simple success message in console
      console.log(`✅ Successfully uploaded ${parsedResumes.length} resume(s)`);
    }
    
    setUploadedFiles(parsedResumes);
  };

  // Perform the AI scan
  const performAIScan = async () => {
    if (userCredits < uploadedFiles.length) {
      alert(`Insufficient credits! You need ${uploadedFiles.length} credits but only have ${userCredits}. Please purchase more credits.`);
      setCurrentPage('pricing');
      return;
    }

    setIsScanning(true);
    
    try {
      const resumesData = uploadedFiles.map(file => ({
        filename: file.name,
        content: file.content
      }));
      
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/scan-resumes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          jobDescription,
          resumes: resumesData,
          keywords
        })
      });

      if (response.status === 401) {
        alert('Session expired. Please log in again.');
        handleLogout();
        return;
      }    
      
      if (!response.ok) {
        throw new Error('Failed to scan resumes');
      }
      
      const data = await response.json();
      
      setUserCredits(data.remainingCredits);
      
      const transformedResults = data.results.map(result => ({
        name: result.filename,
        score: result.overallScore,
        matchDetails: {
          experienceMatch: result.experienceAnalysis.alignment === 'Exceeds' || result.experienceAnalysis.alignment === 'Meets',
          titleMatch: result.keywordAnalysis.mustHave.matchRate !== '0%',
          skillsMatch: [...result.keywordAnalysis.mustHave.found, ...result.keywordAnalysis.niceToHave.found],
          educationMatch: result.educationAnalysis.alignment === 'Exceeds' || result.educationAnalysis.alignment === 'Meets',
          keywordMatches: result.keywordAnalysis.softSkills.found
        },
        summary: {
          recommendation: result.recommendation,
          strengths: result.strengths,
          gaps: result.redFlags
        },
        fullAnalysis: result
      }));
      
      // Sort by score descending (highest to lowest)
      transformedResults.sort((a, b) => b.score - a.score);
      
      setScanResults(transformedResults);
      setIsScanning(false);
      setShowResults(true);
      
    } catch (error) {
      console.error('Error scanning resumes:', error);
      alert('Failed to scan resumes. Please try again.');
      setIsScanning(false);
    }
  };
  
  // Landing Page
  if (currentPage === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        <nav className="bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-center py-4">
              <Logo />
              <div className="space-x-4">
                <button
                  onClick={() => setCurrentPage('pricing')}
                  className="text-gray-600 hover:text-gray-900 font-medium"
                >
                  Pricing
                </button>
                <button
                  onClick={() => setCurrentPage('login')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 py-16 flex-grow">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              AI-Powered Resume Screening
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Save 90% of your screening time with intelligent candidate matching
            </p>
            <p className="text-lg text-green-600 font-semibold mb-2">
              Start with 50 FREE resume scans
            </p>
            <p className="text-sm text-gray-600 mb-8">
              No credit card required - we never store candidate data
            </p>
            <button
              onClick={() => setCurrentPage('signup')}
              className="bg-green-500 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-green-600"
            >
              Start Free - Get 50 Scans
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-3xl mb-3 text-center">🎯</div>
              <h3 className="text-xl font-semibold mb-2">Weighted Scoring</h3>
              <p className="text-gray-600">Evidence-based analysis with must-have, nice-to-have, and soft skills breakdown</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-3xl mb-3 text-center">🔒</div>
              <h3 className="text-xl font-semibold mb-2">Privacy First</h3>
              <p className="text-gray-600">No resume storage - candidate data is never saved</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-3xl mb-3 text-center">⏱️</div>
              <h3 className="text-xl font-semibold mb-2">Instant Rankings</h3>
              <p className="text-gray-600">Get ranked candidates in minutes, not days. At $0.13-$0.30 per resume, one hire pays for hundreds of scans. Focus on strategy, not screening.</p>
            </div>          
          </div>

          {/* Testimonials Section */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-center mb-12">Trusted by HR Professionals</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <div className="text-yellow-400 text-xl">★★★★★</div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "We've screened over 500 candidates using this tool. The privacy-first approach gives us confidence, and the cost savings are remarkable compared to enterprise ATS systems."
                </p>
                <p className="font-semibold">Sarah Mitchell</p>
                <p className="text-sm text-gray-500">HR Director, Tech Startup</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <div className="text-yellow-400 text-xl">★★★★★</div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The AI analysis is incredibly detailed. What used to take me 4 hours now takes 5 minutes. No data retention means we stay compliant without extra effort."
                </p>
                <p className="font-semibold">Michael Chen</p>
                <p className="text-sm text-gray-500">Talent Acquisition Manager</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <div className="text-yellow-400 text-xl">★★★★★</div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Finally, an affordable solution that doesn't compromise on quality or security. The combination of speed and detailed analysis is exactly what we needed."
                </p>
                <p className="font-semibold">Jennifer Torres</p>
                <p className="text-sm text-gray-500">Small Business Owner</p>
              </div>
            </div>

            <div className="mt-12 text-center">
              <div className="inline-flex items-center space-x-8 bg-white py-6 px-12 rounded-lg shadow-md">
                <div>
                  <p className="text-3xl font-bold text-blue-600">1,000+</p>
                  <p className="text-gray-600">Happy Recruiters</p>
                </div>
                <div className="border-l border-gray-300 h-12"></div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">50,000+</p>
                  <p className="text-gray-600">Resumes Analyzed</p>
                </div>
                <div className="border-l border-gray-300 h-12"></div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">Zero</p>
                  <p className="text-gray-600">Data Breaches</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  // Success Page (after purchase)
  if (currentPage === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex flex-col">
        <nav className="bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-center py-4">
              <Logo />
              <button
                onClick={() => setCurrentPage('dashboard')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-4 py-16 flex-grow">
          <div className="bg-white rounded-lg shadow-xl p-8 text-center">
            <div className="mb-6">
              <svg className="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Thank You for Your Purchase!
            </h1>
            
            {purchaseDetails ? (
              <div className="mb-6">
                <p className="text-xl text-gray-600 mb-2">
                  Your purchase is confirmed!
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-lg font-semibold text-green-800">
                    {purchaseDetails.credits} credits added to your account
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Package: {purchaseDetails.package} - ${purchaseDetails.amount}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-xl text-gray-600 mb-6">
                Your credits have been added to your account.
              </p>
            )}
        
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-center mb-4">
                <svg className="h-12 w-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="16" r="1.5" fill="currentColor"/>
                </svg>
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">
                Your Privacy is Our Priority
              </h3>
              <p className="text-gray-700 text-sm mb-2">
                We want to remind you that we <strong>never store resume data, job descriptions, or candidate information</strong>. 
                All applicant data is processed in real-time and immediately discarded.
              </p>
              <p className="text-gray-600 text-sm">
                We will never sell, share, or use your data for anything other than providing you with our service.
              </p>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-semibold text-gray-800 mb-3">What We Store:</h3>
              <div className="text-sm text-left max-w-md mx-auto space-y-2">
                <div className="flex items-start">
                  <span className="text-green-500 mr-2 font-bold">✓</span>
                  <span className="text-gray-600">Your account information (email, name)</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2 font-bold">✓</span>
                  <span className="text-gray-600">Credit balance and transaction history</span>
                </div>
                <div className="flex items-start">
                  <span className="text-red-500 mr-2 font-bold">✗</span>
                  <span className="text-gray-600"><strong>Resume content - NEVER stored</strong></span>
                </div>
                <div className="flex items-start">
                  <span className="text-red-500 mr-2 font-bold">✗</span>
                  <span className="text-gray-600"><strong>Job descriptions - NEVER stored</strong></span>
                </div>
                <div className="flex items-start">
                  <span className="text-red-500 mr-2 font-bold">✗</span>
                  <span className="text-gray-600"><strong>Candidate information - NEVER stored</strong></span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setCurrentPage('scanner')}
              className="mt-8 bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 font-semibold text-lg"
            >
              Start Scanning Resumes
            </button>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  // Pricing Page
  if (currentPage === 'pricing') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <nav className="bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-center py-4">
              <Logo />
              <div className="space-x-4">
                {currentUser ? (
                  <>
                    <span className="text-gray-600">Credits: {userCredits}</span>
                    <button
                      onClick={() => setCurrentPage('dashboard')}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      Dashboard
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setCurrentPage('landing')}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    Back
                  </button>
                )}
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 py-12 flex-grow">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Simple, Transparent Pricing</h1>
            <p className="text-xl text-gray-600 mb-2">Pay only for what you use. No subscriptions.</p>
            <p className="text-lg text-green-600 font-semibold">New users get 50 FREE resume scans</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {creditPackages.map((pkg) => (
              <div 
                key={pkg.id} 
                className={`bg-white rounded-lg shadow-lg p-6 relative ${
                  pkg.popular ? 'border-4 border-blue-500 transform scale-105' : 'border border-gray-200'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap shadow-md">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
                  
                  <div className="mb-4">
                    <span className="text-4xl font-bold">${pkg.price}</span>
                  </div>
                  
                  <div className="mb-6">
                    <p className="text-xl font-semibold text-blue-600 mb-1">
                      {pkg.scans} Resume Scans
                    </p>
                    <p className="text-sm text-gray-500">
                      ${(pkg.price / pkg.scans).toFixed(2)} per resume
                    </p>
                  </div>

                  <ul className="text-left text-sm text-gray-600 mb-6 space-y-2">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      Detailed AI analysis
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      Weighted scoring
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      No candidate data storage
                    </li>
                  </ul>
                  
                  <button
                    onClick={() => {
                      if (!currentUser) {
                        alert('Please sign up or log in first');
                        setCurrentPage('signup');
                      } else {
                        handlePurchaseCredits(pkg.id);
                      }
                    }}
                    className={`w-full py-3 rounded-lg font-semibold ${
                      pkg.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    }`}
                  >
                    Purchase Now
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-blue-50 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-4 text-center">Why Choose Us?</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl mb-2">⚡</div>
                <h4 className="font-semibold mb-2">90% Time Savings</h4>
                <p className="text-sm text-gray-600">Screen 50 resumes in 2 minutes vs. 4+ hours manually</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">🔒</div>
                <h4 className="font-semibold mb-2">Privacy First</h4>
                <p className="text-sm text-gray-600">Zero candidate data storage. Resumes never saved.</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">💰</div>
                <h4 className="font-semibold mb-2">No Subscriptions</h4>
                <p className="text-sm text-gray-600">Pay only for what you use. Credits never expire.</p>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  // Login/Signup Page
  if (currentPage === 'login' || currentPage === 'signup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
          <div className="text-center mb-6">
            <Logo />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-center">
            {currentPage === 'login' ? 'Sign In' : 'Create Account'}
          </h2>
          {currentPage === 'signup' && (
            <p className="text-green-600 font-semibold mb-4 text-center">
              Get 50 FREE resume scans!
            </p>
          )}
          <form onSubmit={handleAuth}>
            {currentPage === 'signup' && (
              <>
                <input
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={formData.firstName || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-lg mb-4"
                />
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  value={formData.lastName || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-lg mb-4"
                />
                <input
                  type="text"
                  name="company"
                  placeholder="Company (Optional)"
                  value={formData.company || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-lg mb-4"
                />
              </>
            )}
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-lg mb-4"
              required
            />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-lg mb-4"
              required
            />
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
            >
              {currentPage === 'login' ? 'Sign In' : 'Sign Up & Get 50 Free Scans'}
            </button>
          </form>
          <button
            onClick={() => setCurrentPage(currentPage === 'login' ? 'signup' : 'login')}
            className="w-full mt-4 text-blue-600 hover:text-blue-800"
          >
            {currentPage === 'login' ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
          </button>
          <button
            onClick={() => setCurrentPage('landing')}
            className="w-full mt-2 text-gray-600 hover:text-gray-900"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Dashboard
  if (currentPage === 'dashboard') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <nav className="bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-center py-4">
              <Logo />
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setCurrentPage('pricing')}
                  className="text-blue-600 hover:text-blue-800 font-semibold"
                >
                  Buy Credits
                </button>
                <span className="text-gray-600">{currentUser?.email}</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
                  {userCredits} credits
                </span>
                <button
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 py-8 flex-grow">
          <h1 className="text-3xl font-bold mb-8">Dashboard</h1>
          
          {userCredits === 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-red-800 mb-2">No Credits Remaining</h3>
              <p className="text-red-600 mb-3">You've used all your free scans. Purchase more to continue.</p>
              <button
                onClick={() => setCurrentPage('pricing')}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
              >
                View Pricing
              </button>
            </div>
          )}
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
            <button
              onClick={() => setCurrentPage('scanner')}
              disabled={userCredits === 0}
              className={`px-6 py-3 rounded-lg font-medium ${
                userCredits === 0
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Start Resume Scanning
            </button>
            {userCredits === 0 && (
              <p className="text-sm text-red-600 mt-2">Purchase credits to start scanning</p>
            )}
          </div>

          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Your Stats</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{userCredits}</p>
                <p className="text-gray-600">Scans Available</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">0</p>
                <p className="text-gray-600">Resumes Scanned</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">0</p>
                <p className="text-gray-600">Hours Saved</p>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  // Scanner Page with Results
  if (currentPage === 'scanner') {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-center py-4">
              <Logo />
              <div className="flex items-center space-x-4">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
                  {userCredits} credits
                </span>
                <button
                  onClick={() => {
                    setCurrentPage('dashboard');
                    setShowResults(false);
                    setScanResults([]);
                    setJobDescription('');
                    setUploadedFiles([]);
                    setKeywords('');
                  }}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 py-8">
          {!showResults ? (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold mb-6">AI Resume Scanner</h2>
              
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Job Description / Requirements
                </label>
                <textarea
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  className="w-full p-3 border rounded-lg h-32"
                  placeholder="Paste the job description here..."
                />
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Priority Keywords (Optional)
                </label>
                <input
                  type="text"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Enter keywords separated by commas (e.g., leadership, agile, MBA)"
                />
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Upload Resumes
                </label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  className="w-full p-3 border rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: PDF, DOC, DOCX • Maximum 100MB per file
                </p>

                {uploadedFiles.length > 0 && (
                  <div className="mt-2">
                    <p className="text-green-600">
                      {uploadedFiles.length} resume(s) uploaded
                    </p>
                    <p className="text-sm text-gray-600">
                      This will use {uploadedFiles.length} credit(s). You have {userCredits} available.
                    </p>
                  </div>
                )}

                {uploadedFiles.length > 100 && (
                  <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>💡 Large batch detected:</strong> Processing {uploadedFiles.length} resumes 
                      will take approximately {Math.ceil(uploadedFiles.length / 10) * 10} seconds (~
                      {Math.ceil(Math.ceil(uploadedFiles.length / 10) * 10 / 60)} minutes). 
                      Please don't refresh the page during processing.
                    </p>
                  </div>
                )}
              </div>

              <button
                onClick={performAIScan}
                disabled={!jobDescription || uploadedFiles.length === 0 || isScanning || userCredits < uploadedFiles.length}
                className={`w-full py-3 rounded-lg font-medium ${
                  !jobDescription || uploadedFiles.length === 0 || userCredits < uploadedFiles.length
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : isScanning
                    ? 'bg-yellow-500 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isScanning ? (
                  <div className="flex items-center justify-center space-x-3">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Analyzing {uploadedFiles.length} resumes... (est. {Math.ceil(uploadedFiles.length * 2.5)}s)</span>
                  </div>
                ) : userCredits < uploadedFiles.length ? (
                  `Insufficient Credits (Need ${uploadedFiles.length}, Have ${userCredits})`
                ) : (
                  'Start AI Scan'
                )}
              </button>

              {userCredits < uploadedFiles.length && uploadedFiles.length > 0 && (
                <button
                  onClick={() => setCurrentPage('pricing')}
                  className="w-full mt-3 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-medium"
                >
                  Buy More Credits
                </button>
              )}

              <p className="mt-4 text-sm text-gray-600 text-center">
                Candidate data is processed securely and never stored
              </p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg shadow-md p-6">
                <style dangerouslySetInnerHTML={{__html: `
                  @media print {
                    nav, .no-print { display: none !important; }
                    .bg-gray-50, .bg-blue-50, .bg-purple-50 { background: white !important; }
                    body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                    @page { margin: 0.75in; }
                  }
                `}} />

                <div className="flex justify-between items-center mb-6 no-print">
                  <h2 className="text-2xl font-bold">Scan Results</h2>
                  <div className="space-x-2">
                    <button
                      onClick={() => window.print()}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium"
                    >
                      Save Full Report
                    </button>
                    <button
                      onClick={() => setShowSummaryView(true)}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 font-medium"
                    >
                      Summary View
                    </button>
                    <button
                      onClick={() => {
                        setShowResults(false);
                        setScanResults([]);
                        setJobDescription('');
                        setUploadedFiles([]);
                        setKeywords('');
                      }}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
                    >
                      New Scan
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-4 no-print text-center">
                  Click "Save Full Report" and your browser will let you save or print this report
                </p>

                <div className="space-y-4">
                  {scanResults.map((candidate, index) => (
                    <div key={index} className="border rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold">{candidate.name}</h3>
                          {candidate.fullAnalysis?.tier && (
                            <p className="text-sm text-gray-500 mt-1">{candidate.fullAnalysis.tier}</p>
                          )}
                        </div>
                        <div className={`px-4 py-2 rounded-full text-white font-semibold text-lg ${
                          candidate.score >= 80 ? 'bg-green-500' :
                          candidate.score >= 60 ? 'bg-yellow-500' :
                          candidate.score >= 40 ? 'bg-orange-500' :
                          'bg-red-500'
                        }`}>
                          {candidate.score}% Match
                        </div>
                      </div>

                      <div className={`p-4 rounded mb-4 ${
                        candidate.fullAnalysis?.recommendation?.includes('Strong Match') ? 'bg-green-50 border border-green-200' :
                        candidate.fullAnalysis?.recommendation?.includes('Good Match') ? 'bg-blue-50 border border-blue-200' :
                        candidate.fullAnalysis?.recommendation?.includes('Partial') ? 'bg-yellow-50 border border-yellow-200' :
                        'bg-red-50 border border-red-200'
                      }`}>
                        <p className="font-semibold text-blue-800">
                          {candidate.fullAnalysis?.recommendation || 'Not Recommended'}
                        </p>
                        <p className="text-gray-700 mt-2">
                          {candidate.fullAnalysis?.executiveSummary || 'No summary available'}
                        </p>
                      </div>

                      {candidate.fullAnalysis?.keywordAnalysis && (
                        <div className="mb-4 grid grid-cols-3 gap-4 text-sm">
                          <div className="p-3 bg-gray-50 rounded text-center">
                            <p className="font-semibold text-gray-700 mb-1">Must-Have Skills</p>
                            <p className="text-3xl font-bold text-blue-600">
                              {candidate.fullAnalysis.keywordAnalysis.mustHave.matchRate}
                            </p>
                          </div>
                          <div className="p-3 bg-gray-50 rounded text-center">
                            <p className="font-semibold text-gray-700 mb-1">Nice-to-Have</p>
                            <p className="text-3xl font-bold text-blue-600">
                              {candidate.fullAnalysis.keywordAnalysis.niceToHave.matchRate}
                            </p>
                          </div>
                          <div className="p-3 bg-gray-50 rounded text-center">
                            <p className="font-semibold text-gray-700 mb-1">Experience</p>
                            <p className="text-3xl font-bold text-blue-600">
                              {candidate.fullAnalysis.experienceAnalysis.alignment}
                            </p>
                          </div>
                        </div>
                      )}

                      {candidate.summary?.strengths.length > 0 && (
                        <div className="mb-3">
                          <p className="font-semibold text-green-600 mb-1">Strengths:</p>
                          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                            {candidate.summary.strengths.map((strength, i) => (
                              <li key={i}>{strength}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {candidate.summary?.gaps.length > 0 && (
                        <div className="mb-3">
                          <p className="font-semibold text-red-600 mb-1">Concerns:</p>
                          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                            {candidate.summary.gaps.map((gap, i) => (
                              <li key={i}>{gap}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="mt-3 pt-3 border-t text-sm text-gray-600">
                        <span className="font-semibold">Skills matched:</span> {
                          candidate.matchDetails.skillsMatch.length > 0
                            ? candidate.matchDetails.skillsMatch.join(', ')
                            : 'None identified'
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {showSummaryView && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 no-print">
                  <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                    <div className="p-6 border-b flex justify-between items-center">
                      <h2 className="text-2xl font-bold">Summary Report - {scanResults.length} Candidates</h2>
                      <div className="space-x-2">
                        <button
                          onClick={() => {
                            const summaryContent = document.getElementById('summary-print-content');
                            const printWindow = window.open('', '_blank');
                            printWindow.document.write('<html><head><title>Summary Report</title><style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-left}th{background:#f3f4f6}</style></head><body>');
                            printWindow.document.write(summaryContent.innerHTML);
                            printWindow.document.write('</body></html>');
                            printWindow.document.close();
                            printWindow.print();
                          }}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                        >
                          Print Summary
                        </button>
                        <button
                          onClick={() => setShowSummaryView(false)}
                          className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                    
                    <div id="summary-print-content" className="p-6 overflow-y-auto">
                      <h1 className="text-xl font-bold mb-4">Candidate Summary Report</h1>
                      <table className="w-full border-collapse border border-gray-300">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 px-4 py-2 text-left">Rank</th>
                            <th className="border border-gray-300 px-4 py-2 text-left">Name</th>
                            <th className="border border-gray-300 px-4 py-2 text-center">Score</th>
                            <th className="border border-gray-300 px-4 py-2 text-left">Recommendation</th>
                            <th className="border border-gray-300 px-4 py-2 text-left">Top Strength</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scanResults.map((candidate, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="border border-gray-300 px-4 py-2 font-bold">#{index + 1}</td>
                              <td className="border border-gray-300 px-4 py-2">{candidate.name}</td>
                              <td className="border border-gray-300 px-4 py-2 text-center">
                                <span className={`font-bold ${
                                  candidate.score >= 80 ? 'text-green-600' :
                                  candidate.score >= 60 ? 'text-yellow-600' :
                                  candidate.score >= 40 ? 'text-orange-600' :
                                  'text-red-600'
                                }`}>
                                  {candidate.score}%
                                </span>
                              </td>
                              <td className="border border-gray-300 px-4 py-2 text-sm">
                                {candidate.fullAnalysis?.recommendation || 'N/A'}
                              </td>
                              <td className="border border-gray-300 px-4 py-2 text-sm">
                                {candidate.summary?.strengths?.[0] || 'See full report'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
}

export default App;
